// src/services/sinaliteService.js
import { collection, getDocs, doc, getDoc } from "./mysqlStore";
import { db } from "./mysqlStore";
import API_BASE from "./apiClient";

const CLOUD_BASE = API_BASE;
const DEFAULT_MARKUP_PERCENT = 35; // used when a product has no custom margin set in the admin panel

const PRODUCTS_CACHE_KEY = "sinalite_products_cache_v1";
const META_CACHE_KEY     = "sinalite_meta_cache_v1";
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes — same window for both caches

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ cachedAt: Date.now(), data }));
  } catch {
    // localStorage full or unavailable — silently skip caching
  }
}

export function applyMarkup(price, markupPercent = DEFAULT_MARKUP_PERCENT) {
  const parsedMarkup = Number(markupPercent);
  const markup = Number.isFinite(parsedMarkup) ? parsedMarkup : DEFAULT_MARKUP_PERCENT;
  const supplierPrice = Number.parseFloat(price);
  if (!Number.isFinite(supplierPrice)) return 0;
  return Math.ceil(supplierPrice * (1 + markup / 100) * 100) / 100;
}

// Load meta (images + descriptions) — cached for 30 min, falls back gracefully
async function loadMeta({ forceRefresh = false } = {}) {
  const cached = !forceRefresh ? readCache(META_CACHE_KEY) : null;
  if (cached) return cached;

  try {
    const [prodSnap, catSnap] = await Promise.all([
      getDocs(collection(db, "productMeta")),
      getDocs(collection(db, "categoryMeta")),
    ]);
    const productMeta  = {};
    const categoryMeta = {};
    prodSnap.docs.forEach(d => { productMeta[d.id]  = d.data(); });
    catSnap.docs.forEach(d  => { categoryMeta[d.id] = d.data(); });
    const result = { productMeta, categoryMeta };
    writeCache(META_CACHE_KEY, result);
    return result;
  } catch {
    // Collections don't exist yet — return empty, products still show
    return { productMeta: {}, categoryMeta: {} };
  }
}

export async function fetchProducts({ forceRefresh = false } = {}) {
  // Try cache first (Sinalite catalog + image/description meta rarely change —
  // avoids slow API + Firestore calls on every page visit)
  const cachedProducts = !forceRefresh ? readCache(PRODUCTS_CACHE_KEY) : null;

  // customProducts (admin-added) always load fresh — these change more often and are small
  const [{ productMeta, categoryMeta }, customSnap] = await Promise.all([
    loadMeta({ forceRefresh }),
    getDocs(collection(db, "customProducts")).catch(() => ({ docs: [] })),
  ]);

  let active;
  if (cachedProducts) {
    active = cachedProducts;
  } else {
    const res = await fetch(`${CLOUD_BASE}/supplier/proxy?path=/product`);
    if (!res.ok) throw new Error("Failed to fetch products");
    const products = await res.json();
    active = products.filter(p => p.enabled === 1);
    writeCache(PRODUCTS_CACHE_KEY, active);
  }

  // Merge meta — imageUrl and description are optional extras
  const enriched = active.map(p => {
    const meta = productMeta[String(p.id)] || {};
    return {
      ...p,
      imageUrl:    meta.imageUrl    || "",
      description: meta.description || "",
      isCustom:    false,
    };
  });

  // Add custom products from MySQL
  const customProducts = customSnap.docs
    .map(d => ({ ...d.data(), id: d.id, isCustom: true }))
    .filter(p => p.enabled !== false);

  const allEnriched = [...enriched, ...customProducts];

  // Group by category
  const productsByCategory = {};
  for (const p of allEnriched) {
    if (!productsByCategory[p.category]) productsByCategory[p.category] = [];
    productsByCategory[p.category].push(p);
  }

  const categories = Object.keys(productsByCategory).sort().map(name => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const meta = categoryMeta[slug] || {};
    return {
      name,
      slug,
      imageUrl:    meta.imageUrl    || "",
      description: meta.description || "",
    };
  });

  return { categories, productsByCategory, allProducts: allEnriched, customProducts };
}

export async function fetchProductOptions(productId) {
  const res = await fetch(
    `${CLOUD_BASE}/supplier/proxy?path=/product/${productId}/en_ca`
  );
  if (!res.ok) throw new Error("Failed to fetch product options");
  const data = await res.json();

  const options = Array.isArray(data[0]) ? data[0] : data;
  const visible = options.filter(o => o.hidden === 0);

  const grouped = {};
  for (const opt of visible) {
    if (!grouped[opt.group]) grouped[opt.group] = [];
    grouped[opt.group].push(opt);
  }

  return { grouped, raw: visible };
}

export async function fetchPrice(productId, selectedIds, categoryName = "") {
  const [res, metaSnap, pricingSnap] = await Promise.all([
    fetch(`${CLOUD_BASE}/supplier/proxy?path=/price/${productId}/en_ca`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productOptions: selectedIds }),
    }),
    getDoc(doc(db, "productMeta", String(productId))).catch(() => null),
    getDoc(doc(db, "siteSettings", "pricing")).catch(() => null),
  ]);
  if (!res.ok) throw new Error("Failed to fetch price");
  const data = await res.json();

  const productMeta = metaSnap && metaSnap.exists() ? metaSnap.data() : {};
  const category = categoryName || productMeta.category || "";
  const categorySlug = String(category).toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const categorySnap = categorySlug
    ? await getDoc(doc(db, "categoryMeta", categorySlug)).catch(() => null)
    : null;
  const categoryMeta = categorySnap && categorySnap.exists() ? categorySnap.data() : {};
  const pricingSettings = pricingSnap && pricingSnap.exists() ? pricingSnap.data() : {};
  const defaultMarkupPercent = pricingSettings.defaultMarkupPercent ?? DEFAULT_MARKUP_PERCENT;
  const markupPercent = productMeta.markupPercent != null
    ? productMeta.markupPercent
    : categoryMeta.markupPercent != null
      ? categoryMeta.markupPercent
      : defaultMarkupPercent;

  const sinalitePrice = parseFloat(data.price) || 0;
  const displayPrice  = applyMarkup(sinalitePrice, markupPercent);

  return {
    sinalitePrice,
    displayPrice,
    markupPercent: Number(markupPercent),
    productOptions: data.productOptions || {},
    packageInfo:    data.packageInfo    || {},
  };
}

export async function fetchShippingEstimate(cartItems, address) {
  const res = await fetch(
    `${CLOUD_BASE}/supplier/proxy?path=/order/shippingEstimate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: cartItems.map(item => ({
          productId: item.productId,
          options:   item.optionMap,
        })),
        shippingInfo: {
          ShipState:   address.state,
          ShipCountry: address.country,
          ShipZip:     address.zip,
        },
      }),
    }
  );
  if (!res.ok) throw new Error("Failed to fetch shipping");
  const data = await res.json();

  return (data.body || []).map(([carrier, service, price, estimatedDays]) => ({
    carrier,
    service,
    price: parseFloat(price) || 0,
    estimatedDays,
  }));
}
